// PersonPopoverCard — the click-anchored popover opened by
// el.call('openPersonPopover', email, anchorEl) (functions/personPopover.js).
// Renders inside the shared @symbo.ls/dropdown DropdownRoot panel (same layer
// AppOrgSwitcherMenu / AppNavbarMoreMenu use) — positioning, auto-flip,
// outside-click and ESC dismiss all come free from that shell-mounted layer.
//
// Content only: a mini profile header (bigger avatar, name, role/title or
// email, presence dot when known) plus a 3x2 grid of icon quick-actions —
// Chat, Call, Book a meeting, Profile, Assign Ticket, Ping. Every action
// reuses an existing registered function via el.call; see _dispatchAction
// below for the reuse + the documented choice behind each one.
//
// Target person travels on `root._personPopoverEmail` (see
// functions/personPopover.js for the full rationale) — read back live via
// findPerson so the card always reflects current roster/presence state
// rather than a frozen snapshot.

import { featureEnabled } from '@symbo-ls/workspace-shared/functions/featureFlags.js'

const _popoverPerson = (el, s) => {
  const email = s.root?._personPopoverEmail
  return email ? el.call('findPerson', email) : null
}

const _isOnline = (s) => {
  const email = s.root?._personPopoverEmail
  if (!email) return false
  return !!(s.root?.presenceById || {})[String(email).toLowerCase()]
}

// §3.7 Directory — the party-by-email cache partyLookup.js fills for an
// address outside the member roster: an object is the match summary,
// `false` is resolved-with-no-match (the Add-to-directory gate), absent is
// never-asked (the popover open fires the resolve).
const _partyOf = (root) => {
  const email = String(root?._personPopoverEmail || '')
    .trim()
    .toLowerCase()
  const v = email ? (root?._partyByEmail || {})[email] : undefined
  return v && typeof v === 'object' ? v : null
}

const _canAddToDirectory = (root, el) => {
  const email = root?._personPopoverEmail
  if (!email || email === root?.currentUserEmail) return false
  // `typeof el.call`, not just `el`: a children factory can re-run against a
  // half-detached element while the dropdown layer tears down, and there the
  // element object still exists but its `call` is already gone.
  if (el && typeof el.call === 'function' && el.call('findPerson', email)) return false
  return (root?._partyByEmail || {})[String(email).trim().toLowerCase()] === false
}

const _ACTIONS = [
  { key: 'chat', icon: 'chat', label: 'workspace.person.action.chat' },
  { key: 'email', icon: 'email', label: 'workspace.person.action.email' },
  { key: 'call', icon: 'phone', label: 'workspace.person.action.call' },
  { key: 'meeting', icon: 'date outline', label: 'workspace.person.action.meet' },
  { key: 'profile', icon: 'user', label: 'workspace.person.action.profile' },
  { key: 'assign', icon: 'check', label: 'workspace.person.action.assign' },
  { key: 'ping', icon: 'bell', label: 'workspace.person.action.ping' },
  { key: 'addDirectory', icon: 'people', label: 'workspace.person.addDirectory' }
]

// Ping doesn't apply to your own card — not reachable through the bottom-left
// dock today (OnlineMembers filters self out already) but kept defensive
// since this popover is meant to be reusable from anywhere with an email.
// Email drops on your own card too (a mail-yourself button is chrome), and
// whenever the `mail` module flag is off — the same gate the header
// envelope reads (architecture/MAIL.md §3.2a entry points).
const _actionsFor = (root, el) => {
  const email = root?._personPopoverEmail
  const isSelf = !!email && email === root?.currentUserEmail
  return _ACTIONS.filter((a) => {
    if (a.key === 'ping' && isSelf) return false
    if (a.key === 'email' && (isSelf || !featureEnabled(root, 'mail'))) return false
    // Only an OUTSIDE address the party resolve answered "no match" for —
    // a member never needs a Party minted off their own card.
    if (a.key === 'addDirectory' && !_canAddToDirectory(root, el)) return false
    return true
  })
}

// One dispatcher for every action row — see the per-branch comments for the
// reuse decision behind each. `s` here is the ACTION row's state (its own
// {key,icon,label}), so `s.root` is the real app root throughout.
async function _dispatchAction(key, el, s) {
  const root = s.root
  const email = root?._personPopoverEmail
  if (!email) return
  el.call('closeDropdown')

  if (key === 'email') {
    // THE §3.2a compose entry point — openMailCompose folds the mail context
    // itself and runs the setup gate, so this branch only shapes the intent
    // (prefilled `to`, with the roster name when the address resolves).
    const person = el.call('findPerson', email)
    el.call('openMailCompose', { to: [{ name: person?.name || '', email }] })
    return
  }

  if (key === 'chat') {
    // Same open-drawer + resolve-or-create-DM shape as AppSidebar's
    // SidebarChatRow — messageUser (packages/chat) finds/creates the 1:1 and
    // sets activeChannelId; MessengerOverlay (mounted at the shell root,
    // gated on _chatDrawerOpen) picks it up and floats the DM open.
    // Fold the chat context first (bundle-fold) — messageUser AND the
    // MessengerOverlay itself live in the lazy chunk.
    const okFold = await el.call('ensureChatContext')
    if (okFold === false) return
    root.update({ _chatDrawerOpen: true })
    try {
      const channelId = await el.call('messageUser', email)
      if (!channelId) throw new Error('messageUser returned no channel')
    } catch (err) {
      console.warn('[PersonPopover] messageUser failed', err)
      el.call('openNotification', {
        type: 'error',
        title: el.call('polyglot', 'workspace.person.chatFailed'),
        message: err?.message || el.call('polyglot', 'workspace.person.tryAgain')
      })
    }
    return
  }

  if (key === 'call') {
    // THE one call (functions/callPerson.js, shared with Fly): a private room,
    // the person made its member, this person joined on /meet, the join link
    // in our DM.
    await el.call('callPerson', el.call('findPerson', email) || email)
    return
  }

  if (key === 'meeting') {
    // CalNewBookingModal is a global @symbo.ls/windows layer now (see
    // workspace/functions/newBookingModal.js) — no more navigating to
    // /calendar first to get a mount for it.
    //
    // The prefilled title is carried over from the calendar rail's own Book
    // button, which this action replaced when the rail stopped mounting its
    // own person card: the key moved from `calendar.person.meetingWith` to
    // this namespace with it, so no copy was lost.
    const person = el.call('findPerson', email)
    el.call('openNewBookingModal', {
      title: el
        .call('polyglot', 'workspace.person.meetingWith')
        .replace('{name}', person?.name || email),
      invitees: email,
      date: el.call('todayISO'),
      type: 'meeting'
    })
    return
  }

  if (key === 'profile') {
    // profilePath is the SAME /profile?u=<email> route PersonCard,
    // PersonDetailModal's "Open profile" and the People page all link to.
    // A card opened in the Messenger (a mention, a member) would open the page
    // UNDER it: the Messenger closes first, the rule a link in a message keeps
    // (chat functions/chatLinkClick.js).
    if (root?._chatDrawerOpen) root.update({ _chatDrawerOpen: false })
    el.router(el.call('profilePath', email), el.getRoot())
    return
  }

  if (key === 'assign') {
    // Simplest-correct choice per spec: jump to the tickets board
    // pre-filtered to this person (the SAME ?assignee= URL-filter contract
    // RoadmapMain's cycle links use) so the user can open or create a
    // ticket and set the assignee themselves — NOT a one-click auto-assign.
    // A fuller "list their open tickets, pick one, sdk.tickets.update(...)"
    // flow is possible later but trades this for two real risks the spec
    // flagged: the tickets metadata-REPLACE trap (must spread existing
    // metadata on every write) and an unresolved "whose open tickets"
    // question (assigner's? assignee's? reporter=me?). This gets the user
    // to the right context with zero write-risk. The Messenger closes first,
    // as for Profile above.
    if (root?._chatDrawerOpen) root.update({ _chatDrawerOpen: false })
    el.router(`/tasks/board?assignee=${encodeURIComponent(email)}`, el.getRoot())
    return
  }

  if (key === 'addDirectory') {
    // partyLookup.js owns the create, the cache write and both toasts; the
    // stashed display name (a mail sender header) beats the bare address as
    // the party's name.
    await el.call('addPartyFromEmail', email, root?._personPopoverName || '')
    return
  }

  if (key === 'ping') {
    // createNotification (shared/functions/notifications/createNotification.js)
    // is the existing SDK-backed creation path — reused as-is, no SDK change
    // needed. Click-through reuses the SAME deep-link shape chat's own
    // @mention notifications already use (chat/functions/subscribeRealtime.js
    // _handleMentionInsert → `/chat#channel=<id>`), which chat's page already
    // parses on mount — so the generic NotificationPanel row click (shared,
    // unmodified) just works: no shared-component edit needed for click-
    // through. Resolving/creating the DM channel up front (before the
    // notification even exists) is what makes that href possible.
    const person = el.call('findPerson', email)
    const me = el.call('findPerson', root?.currentUserEmail)
    // No name to put in `{name}` → the sentence without it. A translated
    // "someone" cannot fill the slot: the Georgian template glues a case
    // ending onto it (glossary rule 7).
    const senderName = me?.name || root?.currentUserEmail || ''
    try {
      // Same lazy-chunk caveat as the 'call' branch: createChannel is folded,
      // not eager. Un-folded it returns undefined, which silently degrades the
      // ping's click-through href from `/chat#channel=<id>` to a bare `/chat`.
      // The fold is best-effort here — a failed fold must NOT block the ping
      // itself, so we carry on with the fallback href rather than returning.
      await el.call('ensureChatContext')
      const channelId = await el.call('createChannel', { kind: 'dm', memberEmails: [email] })
      const notifId = await el.call('createNotification', {
        userEmail: email,
        type: 'ping',
        title: senderName
          ? el.call('polyglot', 'workspace.person.pingedYou').replace('{name}', senderName)
          : el.call('polyglot', 'workspace.person.pingedYouAnon'),
        href: channelId ? `/chat#channel=${channelId}` : '/chat'
      })
      // createNotification resolves `null` (not a throw) on a server-side
      // failure — an unchecked await here is exactly how a previous version
      // of this code showed "Ping sent" even when nothing was created.
      // Treat a falsy id as failure, matching the createRoom check above.
      if (!notifId) {
        el.call('openNotification', {
          title: el.call('polyglot', 'workspace.person.pingFailedSend'),
          message: el.call('polyglot', 'workspace.person.tryAgain'),
          type: 'error'
        })
        return
      }
      el.call('openNotification', {
        title: el.call('polyglot', 'workspace.person.pingSent'),
        message: el
          .call('polyglot', 'workspace.person.pingSentBody')
          .replace('{name}', person?.name || email),
        type: 'success'
      })
    } catch (err) {
      console.warn('[PersonPopover] ping failed', err)
      el.call('openNotification', {
        title: el.call('polyglot', 'workspace.person.pingFailed'),
        message: err?.message || el.call('polyglot', 'workspace.person.tryAgain'),
        type: 'error'
      })
    }
  }
}

export const PersonPopoverCard = {
  // House dropdown-body chrome (round/theme/shadow/padding) — same recipe
  // AppOrgSwitcherMenu / AppNavbarMoreMenu extend; the DropdownRoot panel
  // wrapper itself is a chromeless positioning shell (menu bodies own their
  // own card look).
  extends: 'MenuPanel',
  tag: 'div',
  flow: 'y',
  gap: 'A',
  padding: 'A',
  width: 'H',
  textAlign: 'left',

  subscribeTo: [
    '_personPopoverEmail',
    '_personPopoverName',
    '_partyByEmail',
    'people',
    'presenceById'
  ],

  // An anchored surface is placed from a rect SNAPSHOT (openDropdown captures
  // the trigger's getBoundingClientRect once), so the coordinate goes stale
  // the moment the page, an inner scroller or the window moves. The house
  // behaviour for that is to DISMISS, not to chase the anchor — the retired
  // SharedPersonCard did neither and stayed put while its row scrolled away.
  //
  // `onDocumentScroll` / `onWindowResize` are the framework-owned primitives
  // (raw document listeners are banned, FA503): registered when this card
  // mounts — i.e. exactly while the popover is open — and torn down with it.
  // Capture phase because scroll events do NOT bubble: a chat message list or
  // any other inner scroller only reaches a capture listener on the document.
  // `passive` because a dismissal must never block scrolling.
  //
  // Bare `closeDropdown` with no id, the same convention the action buttons
  // below use: this card only exists while THIS popover is the open layer.
  onDocumentScroll: {
    capture: true,
    passive: true,
    handler: (ev, el) => {
      const t = ev && ev.target
      // Never dismiss on a scroll inside the card itself — the plugin Panel
      // brings its own maxHeight scroller.
      if (t && t.nodeType === 1 && el.node && el.node.contains(t)) return
      el.call('closeDropdown')
    }
  },
  onWindowResize: (ev, el) => el.call('closeDropdown'),

  Header: {
    flow: 'x',
    align: 'center left',
    gap: 'Z1',

    Avatar: {
      boxSize: 'avatarXl',
      round: 'radiusPill',
      flexShrink: '0',
      align: 'center center',
      src: (el, s) => el.call('avatarUrlFor', s.root?._personPopoverEmail || '')
    },

    Info: {
      flow: 'y',
      gap: 'V',
      flex: '1',
      minWidth: '0',

      Name: {
        tag: 'span',
        fontSize: 'A',
        fontWeight: '700',
        color: 'title',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
        text: (el, s) =>
          _popoverPerson(el, s)?.name ||
          _partyOf(s.root)?.name ||
          s.root?._personPopoverName ||
          s.root?._personPopoverEmail ||
          el.call('polyglot', 'workspace.person.unknown')
      },

      Sub: {
        tag: 'span',
        fontSize: 'Z2',
        color: 'caption',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
        text: (el, s) => _popoverPerson(el, s)?.position || s.root?._personPopoverEmail || ''
      },

      Presence: {
        // `hide:` (not `if:`) — presence syncs after mount; `if` would freeze
        // this hidden forever for a person who comes online while the
        // popover happens to already be open (same trap OnlineMembers'
        // header comment documents for its own widget-level hide).
        hide: (el, s) => !_isOnline(s),
        flow: 'x',
        align: 'center left',
        gap: 'V',
        marginTop: 'X',

        Dot: {
          width: 'Y',
          height: 'Y',
          round: '50%',
          background: 'green'
        },
        Label: {
          tag: 'span',
          fontSize: 'Y2',
          color: 'caption',
          text: '{{ workspace.person.online | polyglot }}'
        }
      },

      // §3.7 Directory — the resolved-party tag for an outside address
      // (same `hide:`-not-`if:` reasoning as Presence: the resolve lands
      // AFTER the popover opened).
      Directory: {
        hide: (el, s) => !_partyOf(s.root),
        flow: 'x',
        align: 'center left',
        gap: 'V',
        marginTop: 'X',

        Icon: { name: 'people', boxSize: 'Z', color: 'caption', flexShrink: '0' },
        Label: {
          tag: 'span',
          fontSize: 'Y2',
          color: 'caption',
          text: '{{ workspace.person.inDirectory | polyglot }}'
        }
      }
    }
  },

  // The actions are set apart by space, not a hairline — no rules inside a
  // rounded surface (the workspace switcher's footer does the same).
  Divider: { height: '1px', flexShrink: '0', margin: 'V 0' },

  Actions: {
    display: 'grid',
    gridTemplateColumns: 'repeat(3, 1fr)',
    gap: 'V',
    childExtends: 'PersonPopoverAction',
    childrenAs: 'state',
    childrenDeps: [
      (el, s) => s.root && s.root._personPopoverEmail,
      (el, s) => s.root && s.root._partyByEmail,
      (el, s) => s.root && s.root.people
    ],
    children: (el, s) => _actionsFor(s.root, el)
  }
}

export const PersonPopoverAction = {
  tag: 'button',
  type: 'button',
  flow: 'y',
  align: 'center center',
  gap: 'V',
  padding: 'Z A',
  round: 'radiusControl',
  borderWidth: '0',
  background: 'transparent',
  cursor: 'pointer',
  color: 'title',
  fontSize: 'Y2',
  // Labels are 500 (the weight law).
  fontWeight: '500',
  ':hover': { background: 'hover' },
  ':active': { background: 'selected' },

  title: (el, s) => el.call('polyglot', s.label || ''),
  'aria-label': (el, s) => el.call('polyglot', s.label || ''),

  // The glyph on the neutral widget plate (was the `overlayStrong` wash).
  IconWrap: {
    width: 'C1',
    height: 'C1',
    round: 'radiusPill',
    align: 'center center',
    background: 'widgetPlate',
    flexShrink: '0',
    Icon: { name: (el, s) => s.icon, boxSize: 'A' }
  },

  Label: { tag: 'span', text: (el, s) => el.call('polyglot', s.label || '') },

  onClick: (e, el, s) => {
    e.stopPropagation()
    _dispatchAction(s.key, el, s)
  }
}
